/* eslint-disable lines-between-class-members */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { computed } from '@ember/object';
import { reads } from '@ember/object/computed';
import { inject as service } from '@ember/service';

import EnvironmentUtils from 'mewe/utils/environment-utils';
import GroupStore from 'mewe/stores/group-store';
import { Theme } from 'mewe/constants';
import { showGroupPreviewDialog } from 'mewe/utils/dialogs-common';

export default class MwAuthor extends Component {
  @service dynamicPopups;
  @service account;
  @service dynamicDialogs;
  @service router;

  @reads('args.post') post;
  @reads('args.owner') owner;
  @reads('args.group') group;
  @reads('args.event') event;
  @reads('args.comment') comment;
  @reads('args.feedTheme') feedTheme;
  @reads('args.inMediaDialog') inMediaDialog;
  @reads('args.page') page;
  @reads('args.postedByPage') postedByPage;
  @reads('args.scope') scope;
  @reads('args.postInPostbox') postInPostbox;
  @reads('args.isSharedPost') isSharedPost;

  imageSize = '150x150';

  @computed('group.id')
  get fullGroup() {
    return GroupStore.getState().groups.find((group) => group.id === this.group?.id);
  }

  @computed('owner.id', 'fullGroup.{ownerId,adminIds}', 'post.{event2,eventInfo}')
  get ownerRole() {
    if (this.args.hideRole) return;
    if (this.isSharedPost) return;
    if (this.showUserAndCommunity) return;

    if (this.post?.event2 && !this.post?.eventInfo) {
      if (this.post.event2.ownerId === this.owner?.id) return __('Owner');
    } else {
      if (this.group?.id === 'contacts' || this.group?.id === 'mycloud' || this.group?.id === 'privacymail') {
        return;
      }

      if (!this.fullGroup) return;

      if (this.fullGroup.ownerId === this.owner?.id) {
        return __('Owner');
      } else if (this.fullGroup.adminIds && this.fullGroup.adminIds.includes(this.owner?.id)) {
        return __('Admin');
      }
    }
  }

  @computed('post.page.isOwnerAdmin', '{isSharedPost,postedByPage}')
  get showPublisher() {
    return this.postedByPage && this.post.page?.isOwnerAdmin && !this.isSharedPost && !this.post.scheduled;
  }

  @computed('post.event2.name', 'group.name', 'post.group.name', 'feedTheme')
  get groupName() {
    // don't show event name in groups feed but group name
    if (this.post?.event2?.name && this.feedTheme !== Theme.GROUPS) return this.post.event2.name;
    else if (this.group.name) return this.group.name;
    else if (this.post?.group.name) return this.post.group.name;
    else if (this.post.group.id === 'contacts') return __('Home');
  }

  @computed('owner.name', 'postedByPage', 'post.page.name')
  get authorName() {
    if (this.postedByPage) return this.post.page?.name;
    return this.owner?.name;
  }

  get authorTitle() {
    if (this.args.showUserAndCommunity) {
      return this.groupName;
    }
    return this.authorName;
  }

  @computed('group.id')
  get isGroupAuthor() {
    const groupId = this.group.id;
    return groupId && groupId !== Theme.CONTACTS;
  }

  @computed('owner', 'group', 'imageSize')
  get avatar() {
    var avatar = this.owner._links.avatar.href;

    if (avatar) {
      avatar = avatar.replace('{imageSize}', this.imageSize);
    } else {
      if (!this.owner.id) {
        return '';
      }

      avatar = `/api/v2/photo/profile/${this.imageSize}/${this.owner.id}?`;

      if (this.group.id) avatar += `group=${this.group.id}&`;

      avatar += `f=${this.owner.fprint || ''}`;
    }

    // I want to have all my avatars set to one fprint (MW)
    if (this.owner.id == this.account.activeUser.id) {
      avatar = avatar.substr(0, avatar.indexOf('f=') + 2) + this.account.activeUser.fprint;
    }

    // avatar link can have different group or no group
    avatar = avatar.replace(new RegExp('\\b(group=).*?(&|$)'), '$1' + (this.group.id || '') + '$2');

    if (avatar[0] === '/') avatar = EnvironmentUtils.getImgHost(true) + avatar;

    return avatar;
  }

  get showJoinOption() {
    if (this.comment) return false;
    if (!this.post?.isGroupPost) return false;
    if (this.args.isRefPost || this.args.postInPostbox || this.args.inMediaDialog) return false;
    return true;
  }

  get showJoinButton() {
    if (!this.showJoinOption) {
      return false;
    }

    return this.post.discoverScope && !this.post.group.isMember && !this.post.group.alreadyApplied;
  }

  get showPendingApproval() {
    if (!this.showJoinOption) {
      return false;
    }

    return this.post.discoverScope && !this.post.group.isMember && this.post.group.alreadyApplied;
  }

  @action
  joinGroup() {
    if (this.post?.customInteraction) {
      return;
    }

    this.dynamicDialogs.closeAll();

    if (this.post.isEventPost) {
      this.router.transitionTo('app.event', this.post?.event2?.id);
    } else if (this.post.isGroupPost && !this.args.postInPostbox) {
      const group = GroupStore.getGroupIfMember(this.group.id);
      if (!group) {
        showGroupPreviewDialog(this.dynamicDialogs, this.group, {
          isDiscoverPostInteraction: !!this.post.discoverScope,
          context: this.args.postContext,
        });
        return false;
      } else {
        this.router.transitionTo('app.group', this.group?.id);
      }
    }
  }

  @action
  showAppliedDialog() {
    this.dynamicDialogs.openDialog('simple-dialog-new', {
      closeBtnText: __(`Got it!`),
      message: __(
        `You have applied to join this group. As soon as the group owner approves, you will be able to visit the group and engage with the posts and members.`
      ),
    });
  }


  @action
  showProfilePopup(target) {
    if (this.owner?.id === this.account.activeUser.id) return;
    if (this.postedByPage) return;
    if (!this.account?.activeUser.id) return;

    if (!this.isDestroyed && !this.isDestroying && !this.args?.post?.isPublicContent) {
      this.dynamicPopups.openPopup('mw-profile-popup', {
        parent: target,
        owner: this.owner,
        group: this.group,
        event: this.post?.event2,
        avatar: this.avatar,
      });
    }
  }
}
